/**
 * This file contains all the NgXs actions.
 */

import {
  BOIType,
  IDocumentType,
  IEntityAutoCollectExclusionMap,
  IEntityAutoCollectUpdate,
  IEntityDocument,
  IEntityMap,
  IEntityProgressMap,
  IEntitySubmitConsent,
  IEntityTotalProgress,
  IGroupAutoCollectExclusionMap,
  IGroupAutoCollectUpdate, IUser, Modify,
  UUID,
} from "src/app/core/interfaces";
import { ICustomEmailContent, IEntity, IEntitySelfBillUpdate, IGroup, Role, } from "src/app/core/interfaces";
import {
  IShareTypeChanges
} from "../../modules/entities/components/cap-table/components/configure-share-types-dialog/configure-share-types-dialog.component";
import {
  IShareRecordChanges
} from "../../modules/entities/components/cap-table/components/configure-share-records-dialog/configure-share-records-dialog.component";

/**
 * @description User actions
 */
export namespace OrgUser {
  export class Update {
    static readonly type = "[OrgUser] Update";
    constructor(public updatedUser: Modify<IUser, { id: UUID }>) { }
  }

  export class UpdateGroupMembership {
    static readonly type = "[OrgUser] Update Group Membership";
    constructor(public userId: UUID, public groupIds: UUID[]) { }
  }

  export class GetAllUsers {
    static readonly type = "[OrgUser] Get All Users";
  }

  export class InviteUser {
    static readonly type = "[OrgUser] Invite User";
    constructor(
      public email: string,
      public role: Role,
      public groupIds: UUID[]
    ) { }
  }

  export class Delete {
    static readonly type = "[OrgUser] Delete User";
    constructor(public uid: string, public role: Role) { }
  }

  export class Reset {
    static readonly type = "[OrgUser] Reset";
  }
}

/**
 * @description Organization actions
 */
export namespace Organization {
  export class Get {
    static readonly type = "[Organization] Get";
    constructor(public orgId: UUID) { }
  }

  export class Update {
    static readonly type = "[Organization] Update";
    constructor(public name: string) { }
  }

  export class UpdateLogo {
    static readonly type = "[Organization] UpdateLogo";
    constructor(public logoUrl: string) { }
  }

  export class SetGlobalSelfBill {
    static readonly type = "[Organization] SetGlobalSelfBill";
    constructor(public selfBilled: boolean) { }
  }

  export class SetDefaultAutoCollect {
    static readonly type = "[Organization] SetDefaultAutoCollect";
    constructor(public defaultAutoCollect: boolean) { }
  }

  export class Reset {
    static readonly type = "[Organization] Reset";
  }

  export class UpdateName {
    static readonly type = "[Organization] UpdateName";
    constructor(public name: string) { }
  }

  export class UpdateContactInformation {
    static readonly type = "[Organization] UpdateContactInformation";
    constructor(
      public orgPhone: string | null,
      public orgEmail: string | null
    ) { }
  }

  export class UpdateCustomEmailContent {
    static readonly type = "[Organization] UpdateCustomEmailContent";
    constructor(public content: ICustomEmailContent) { }
  }

  export class UpdateEnterpriseFeaturesEnabled {
    static readonly type = "[Organization] UpdateEnterpriseFeaturesEnabled";
    constructor(public enterpriseFeaturesEnabled: boolean) { }
  }
}

/**
 * @description Group actions
 */
export namespace Group {
  export class LoadAll {
    static readonly type = "[Group] Load All Groups";
  }

  export class Upsert {
    static readonly type = "[Group] Upsert Group";
    constructor(public groupId: UUID, public group: IGroup) { }
  }

  export class Delete {
    static readonly type = "[Group] Delete Group";
    constructor(public groupId: UUID) { }
  }

  export class LoadAutoCollectExclusions {
    static readonly type = "[Group] Load Auto Collect Exclusions";
  }

  export class LoadAutoCollectExclusionsSuccess {
    static readonly type = "[Group] Load Auto Collect Exclusions Success";
    constructor(public groupAutoCollectExclusionMap: IGroupAutoCollectExclusionMap) { }
  }

  export class AutoCollectExclusion {
    static readonly type = "[Group] Auto Collect Exclusion";
    constructor(public groupUpdate: IGroupAutoCollectUpdate) { }
  }

  export class AutoCollectExclusionSuccess {
    static readonly type = "[Group] Auto Collect Exclusion Success";
    constructor(public groupUpdate: IGroupAutoCollectUpdate) { }
  }
}

/**
 * @description Entity actions
 */
export namespace Entity {
  export class GetEntity {
    static readonly type = "[Entity] Get";
    constructor(public entityId: UUID) { }
  }
  export class GetEntitySuccess {
    static readonly type = "[Entity] Get Success";
    constructor(public entity: IEntity) { }
  }
  export class GetEntityFail {
    static readonly type = "[Entity] Get Fail";
    constructor(public error: unknown) { }
  }

  export class GetEntities {
    static readonly type = "[Entity] Get Entities";
    constructor(public groupIds: UUID[]) { }
  }
  export class GetEntitiesSuccess {
    static readonly type = "[Entity] Get Entities Success";
    constructor(public entities: IEntityMap) { }
  }
  export class GetEntitiesFail {
    static readonly type = "[Entity] Get Entities Fail";
    constructor(public error: unknown) { }
  }

  export class Create {
    static readonly type = "[Entity] Create";
    constructor(public entity: IEntity) { }
  }
  export class CreateSuccess {
    static readonly type = "[Entity] Create Success";
    constructor(public entity: IEntity) { }
  }
  export class CreateFail {
    static readonly type = "[Entity] Create Fail";
    constructor(public error: unknown) { }
  }

  export class Update {
    static readonly type = "[Entity] Update";
    constructor(public entity: IEntity) { }
  }
  export class UpdateSuccess {
    static readonly type = "[Entity] Update Success";
    constructor(public updatedEntity: IEntity, public message = "Entity updated") { }
  }
  export class UpdateFail {
    static readonly type = "[Entity] Update Fail";
    constructor(public error: unknown) { }
  }

  export class AddEntityToGroup {
    static readonly type = "[Entity] Add Entity To Group";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public entityLegalName: string,
      public groupId: UUID
    ) { }
  }
  export class AddEntityToGroupSuccess {
    static readonly type = "[Entity] Add Entity To Group Success";
    constructor(public updatedEntity: IEntity) { }
  }
  export class AddEntityToGroupFail {
    static readonly type = "[Entity] Add Entity To Group Fail";
    constructor(public error: unknown) { }
  }

  export class MoveEntityToGroup {
    static readonly type = "[Entity] Move Entity To Group";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public entityLegalName: string,
      public fromGroupId: UUID,
      public toGroupId: UUID
    ) { }
  }
  export class MoveEntityToGroupSuccess {
    static readonly type = "[Entity] Move Entity To Group Success";
    constructor(public updatedEntity: IEntity) { }
  }
  export class MoveEntityToGroupFail {
    static readonly type = "[Entity] Move Entity To Group Fail";
    constructor(public error: unknown) { }
  }

  export class AddOwnerToEntity {
    static readonly type = "[Entity] Add Owner To Entity";
    constructor(
      public entityId: UUID,
      public ownerId: UUID,
      public boiType: BOIType
    ) { }
  }
  export class AddOwnerToEntitySuccess {
    static readonly type = "[Entity] Add Owner To Entity Success";
    constructor(public entity: IEntity) { }
  }
  export class AddOwnerToEntityFail {
    static readonly type = "[Entity] Add Owner To Entity Fail";
    constructor(public error: unknown) { }
  }

  export class RemoveOwnerFromEntity {
    static readonly type = "[Entity] Remove Owner From Entity";
    constructor(
      public entityId: UUID,
      public ownerId: UUID,
      public boiType: BOIType
    ) { }
  }
  export class RemoveOwnerFromEntitySuccess {
    static readonly type = "[Entity] Remove Owner From Entity Success";
    constructor(public entity: IEntity) { }
  }
  export class RemoveOwnerFromEntityFail {
    static readonly type = "[Entity] Remove Owner From Entity Fail";
    constructor(public error: unknown) { }
  }

  export class GetTotalEntityProgress {
    static readonly type = "[Entity] Get Total Entity Progress";
    constructor(
      public entityId: UUID,
      public ownerEntityIds: UUID[] = [],
      public isNeedUpdateEntityInFirestore = true
    ) { }
  }
  export class GetTotalEntityProgressSuccess {
    static readonly type = "[Entity] Get Total Entity Progress Success";
    constructor(
      public entityId: UUID,
      public totalProgress: IEntityTotalProgress,
      public ownerEntityProgressMap: IEntityProgressMap = {}
    ) { }
  }
  export class GetTotalEntityProgressFail {
    static readonly type = "[Entity] Get Total Entity Progress Fail";
    constructor(public error: unknown) { }
  }

  export class TransferToSecureFile {
    static readonly type = "[Entity] Transfer To Secure File";
    constructor(public entityId: UUID, public ownerId: UUID) { }
  }

  export class TransferToSecureFileSuccess {
    static readonly type = "[Entity] Transfer To Secure File Success";
    constructor(public transferredEntity: IEntity) { }
  }

  export class TransferToSecureFileFail {
    static readonly type = "[Entity] Transfer To Secure File Fail";
    constructor(public error: unknown) { }
  }

  export class InstantlyTransferToSecureFile {
    static readonly type = "[Entity] Instantly Transfer To Secure File";
    constructor(
      public entityId: UUID,
      public userId: UUID,
      public email: string,
      public displayName: string
    ) { }
  }

  export class InstantlyTransferToSecureFileSuccess {
    static readonly type = "[Entity] Instantly Transfer To Secure File Success";
    constructor(public transferredEntity: IEntity) { }
  }

  export class InstantlyTransferToSecureFileFail {
    static readonly type = "[Entity] Instantly Transfer To Secure File Fail";
    constructor(public error: unknown) { }
  }

  export class RevokeSecureFileInvite {
    static readonly type = "[Entity] Revoke Secure File Invite";
    constructor(public entityId: UUID) { }
  }

  export class RevokeSecureFileInviteSuccess {
    static readonly type = "[Entity] Revoke Secure File Invite Success";
    constructor(public revokedEntity: IEntity) { }
  }

  export class RevokeSecureFileInviteFail {
    static readonly type = "[Entity] Revoke Secure File Invite Fail";
    constructor(public error: unknown) { }
  }

  export class Delete {
    static readonly type = "[Entity] Delete";
    constructor(public entityId: UUID) { }
  }
  export class DeleteSuccess {
    static readonly type = "[Entity] Delete Success";
    constructor(public entityId: UUID) { }
  }
  export class DeleteFail {
    static readonly type = "[Entity] Delete Fail";
    constructor(public error: unknown) { }
  }

  export class SelfBill {
    static readonly type = "[Entity] Self Bill";
    constructor(public entityUpdates: IEntitySelfBillUpdate[]) { }
  }

  export class SelfBillSuccess {
    static readonly type = "[Entity] Self Bill Success";
  }

  export class LoadAutoCollectExclusions {
    static readonly type = "[Entity] Load Auto Collect Exclusions";
  }

  export class LoadAutoCollectExclusionsSuccess {
    static readonly type = "[Entity] Load Auto Collect Exclusions Success";
    constructor(public entityAutoCollectExclusionMap: IEntityAutoCollectExclusionMap) { }
  }

  export class AutoCollectExclusion {
    static readonly type = "[Entity] Auto Collect Exclusion";
    constructor(public entityUpdate: IEntityAutoCollectUpdate) { }
  }

  export class AutoCollectExclusionSuccess {
    static readonly type = "[Entity] Auto Collect Exclusion Success";
    constructor(public entityUpdate: IEntityAutoCollectUpdate) { }
  }

  export class SubmitToFinCEN {
    static readonly type = "[Entity] Submit To FinCEN";
    constructor(
      public entityId: UUID,
      public consentData: IEntitySubmitConsent,
      public isSubmittedByUser: boolean
    ) { }
  }
  export class SubmitToFinCENSuccess {
    static readonly type = "[Entity] Submit To FinCEN Success";
    constructor(
      public consentData: IEntitySubmitConsent,
      public isSubmittedByUser: boolean
    ) { }
  }
  export class SubmitToFinCENFail {
    static readonly type = "[Entity] Submit To FinCEN Fail";
    constructor(public error: unknown) { }
  }

  export class UpdateStripeSubscription {
    static readonly type = "[Entity] Update Stripe Subscription";
    constructor(public entityId: UUID, public subscriptionId: string) { }
  }

  export class UpdateCapTable {
    static readonly type = "[Entity] Update Capitalization Table";
    constructor(public entityId: UUID, public orgId: UUID, public shareTypeChanges: IShareTypeChanges) { }
  }

  export class ManageCapTableRecords {
    static readonly type = "[Entity] Manage Capitalization Table Records";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public shareTypeId: UUID,
      public shareRecordChanges: IShareRecordChanges,
    ) { }
  }

  export class UpdateDocumentTypes {
    static readonly type = "[Entity] Update Document Types";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public updatedDocTypes: IDocumentType[]
    ) { }
  }

  export class AddDocumentsToType {
    static readonly type = "[Entity] Add Documents To Type";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public documentTypeId: UUID,
      public documents: IEntityDocument[],
    ) { }
  }

  export class MoveDocumentBetweenTypes {
    static readonly type = "[Entity] Move Document Between Types";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public fromDocumentTypeId: UUID,
      public toDocumentTypeId: UUID,
      public documentId: UUID,
    ) { }
  }

  export class RemoveDocumentFromType {
    static readonly type = "[Entity] Remove Document From Type";
    constructor(
      public orgId: UUID,
      public entityId: UUID,
      public documentTypeId: UUID,
      public document: IEntityDocument,
    ) { }
  }
}
